import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import type { Clients, Projects } from '@/API'
import * as queries from '../graphql/queries'
import { makeErrorToast, msg } from './makeToast'

async function getClientRoute({
	currentLocation,
	id,
	tenantId,
}: {
	currentLocation: string
	id: string
	tenantId: string
}) {
	try {
		const {
			data: { getClients },
		} = (await API.graphql(
			graphqlOperation(queries.getClients, {
				id: id,
			})
		)) as { data: { getClients: Clients } }
		if (
			getClients &&
			getClients.activeFlag === true &&
			getClients.tenantId === tenantId
		) {
			return { route: currentLocation, label: getClients.name }
		}
		return { route: '/', label: 'Client Not Found' }
	} catch (err: unknown) {
		console.error(err)
		makeErrorToast(msg)
		return { route: '/', label: 'Client Not Found' }
	}
}

async function getProjectRoute({
	currentLocation,
	id,
	tenantId,
}: {
	currentLocation: string
	id: string
	tenantId: string
}) {
	const routes: { route: string; label: string }[] = []
	try {
		const graphqlResult = await API.graphql(
			graphqlOperation(queries.getProjects, {
				id: id,
			})
		)

		const {
			data: { getProjects },
		} = graphqlResult as { data: { getProjects: Projects } }
		if (
			getProjects &&
			getProjects.activeFlag === true &&
			getProjects.tenantId === tenantId
		) {
			routes.push({ route: currentLocation, label: getProjects.name })

			if (getProjects.parentClientId) {
				const clientRoute = await getClientRoute({
					currentLocation: `/clients/${getProjects.parentClientId}`,
					id: getProjects.parentClientId,
					tenantId,
				})
				routes.push(clientRoute)
			} else if (getProjects.parentProjectId) {
				const parentProjectRoute = await getProjectRoute({
					currentLocation: `/projects/${getProjects.parentProjectId}`,
					id: getProjects.parentProjectId,
					tenantId,
				})
				routes.push(...(parentProjectRoute ?? []))
			} else {
				captureExceptionAndThrow(
					new Error('Parent project or client not found'),
					{
						location: 'breadcrumbUtil.getProjectRoute',
						projectId: id,
						tenantId,
						currentLocation,
					}
				)
			}
			return routes
		}
		routes.push({ route: '/', label: 'Project Not Found' })
		return routes
	} catch (err) {
		console.error('getProjectRoute error:', err)
		makeErrorToast(msg)
		return []
	}
}

async function getFreelanceProjectRoute({
	currentLocation,
	id,
	tenantId,
}: {
	currentLocation: string
	id: string
	tenantId: string
}) {
	const routes: { route: string; label: string }[] = []
	try {
		const {
			data: { getProjects: projectData },
		} = (await API.graphql(
			graphqlOperation(queries.getProjects, { id: id })
		)) as {
			data: { getProjects: Projects }
		}
		if (projectData && projectData.activeFlag === true) {
			routes.push({ route: currentLocation, label: projectData.name })
		}
		if (projectData.parentProjectId) {
			const parentProjectRoute = await getFreelanceProjectRoute({
				currentLocation: `/projects/${projectData.parentProjectId}`,
				id: projectData.parentProjectId,
				tenantId,
			})
			routes.push(...parentProjectRoute)
		}
		return routes
	} catch (err) {
		makeErrorToast(msg)
		return []
	}
}

export { getClientRoute, getFreelanceProjectRoute, getProjectRoute }
